import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { classifyHttpFailure } from '../observability/error-classification';
import { urlTemplate } from '../observability/url-template';
import { DatadogRumService } from '../vendor/datadog/datadog-rum.service';

/** One name with facets, never one per route — the route is high-cardinality and belongs in context. */
const HTTP_REQUEST_VITAL = 'http.request';

/**
 * Times every request as a RUM duration vital: issued → response usable, bridge marshalling included.
 * Registered *outside* `capacitorHttpInterceptorFn` so native calls are timed too — `CapacitorHttp`
 * never touches `XMLHttpRequest`/`fetch`, which is all RUM resources see. Errors pass through untouched.
 */
export function httpLatencyInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const rum = inject(DatadogRumService);

  // Concurrent requests share the vital's name, so each needs its own key or the later `start` wins.
  // Not the request id: `sessionInterceptorFn` retries a rejected Access Token by re-subscribing to
  // this chain with the same one, so two attempts would collide on it. It rides the context instead.
  const vitalKey = uuidv4();
  const requestId = request.headers.get('x-ninety-request-id');
  const route = urlTemplate(request.url);

  rum.startDurationVital(HTTP_REQUEST_VITAL, { vitalKey });

  // Closed in `finalize`, not the terminal handlers: a switchMap-cancelled request never reaches those, and an
  // unstopped key sits in the RUM tracker until session renewal. Latency queries should filter `outcome:success`.
  let context: Record<string, unknown> = { method: request.method, route, requestId, outcome: 'cancelled' };

  return next(request).pipe(
    tap(event => {
      if (!(event instanceof HttpResponse)) return;
      context = { method: request.method, route, requestId, status: event.status, outcome: 'success' };
    }),
    catchError((err: unknown) => {
      const status = (err as { status?: number })?.status;
      const { reason } = classifyHttpFailure(status);
      context = { method: request.method, route, requestId, status, outcome: 'failure', reason };
      return throwError(() => err);
    }),
    finalize(() => rum.stopDurationVital(HTTP_REQUEST_VITAL, { vitalKey, context }))
  );
}
